import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Users, KeyRound, Shield, Search, Eye, EyeOff } from 'lucide-react';
import api from '@/lib/api';

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700 border-red-200',
  hr: 'bg-purple-100 text-purple-700 border-purple-200',
  manager: 'bg-blue-100 text-blue-700 border-blue-200',
  employee: 'bg-slate-100 text-slate-600 border-slate-200',
};

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [resetDialog, setResetDialog] = useState({ open: false, user: null });
  const [roleDialog, setRoleDialog] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/user-management/list');
      setUsers(data);
    } catch {
      toast.error('Failed to load users');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filtered = users.filter(u => {
    const matchSearch = !search ||
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.username?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setActionLoading(true);
    try {
      await api.post('/api/user-management/reset-password', {
        user_id: resetDialog.user.id,
        new_password: newPassword,
      });
      toast.success(`Password reset for ${resetDialog.user.name}`);
      setResetDialog({ open: false, user: null });
      setNewPassword('');
      setShowPw(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset password');
    }
    setActionLoading(false);
  };

  const handleUpdateRole = async () => {
    if (!newRole) { toast.error('Select a role'); return; }
    setActionLoading(true);
    try {
      await api.post('/api/user-management/update-role', {
        user_id: roleDialog.user.id,
        role: newRole,
      });
      toast.success(`Role updated to ${newRole} for ${roleDialog.user.name}`);
      setRoleDialog({ open: false, user: null });
      setNewRole('');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    }
    setActionLoading(false);
  };

  const stats = {
    total: users.length,
    admins: users.filter(u => u.role === 'admin').length,
    managers: users.filter(u => u.role === 'manager').length,
    hr: users.filter(u => u.role === 'hr').length,
    employees: users.filter(u => u.role === 'employee').length,
  };

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div>
        <h1 className="text-2xl font-bold text-[#0B1B33]">User Management</h1>
        <p className="text-sm text-slate-500 mt-1">Manage employee login credentials and roles</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Total Users', value: stats.total, icon: Users, color: 'text-[#0D2850]' },
          { label: 'Admins', value: stats.admins, icon: Shield, color: 'text-red-600' },
          { label: 'Managers', value: stats.managers, icon: Users, color: 'text-blue-600' },
          { label: 'HR', value: stats.hr, icon: Users, color: 'text-purple-600' },
          { label: 'Employees', value: stats.employees, icon: Users, color: 'text-slate-600' },
        ].map(s => (
          <Card key={s.label} className="border-slate-200">
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`w-5 h-5 ${s.color}`} />
              <div>
                <p className="text-2xl font-bold text-[#0B1B33]">{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, username, or email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
                data-testid="user-search-input"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="role-filter-select">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="hr">HR</SelectItem>
                <SelectItem value="employee">Employee</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            All Users ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold text-slate-600">Username</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-600">Name</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-600 hidden sm:table-cell">Email</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-600">Role</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-600 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(u => (
                    <TableRow key={u.id} data-testid={`user-row-${u.username || u.id}`}>
                      <TableCell className="font-mono text-sm font-semibold text-[#0D2850]">
                        {u.username || '-'}
                      </TableCell>
                      <TableCell className="text-sm">{u.name}</TableCell>
                      <TableCell className="text-sm text-slate-500 hidden sm:table-cell">{u.email || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs capitalize ${ROLE_COLORS[u.role] || ''}`}>
                          {u.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7 gap-1 px-2 sm:px-3"
                            onClick={() => { setResetDialog({ open: true, user: u }); setNewPassword(''); setShowPw(false); }}
                            data-testid={`reset-pwd-btn-${u.username || u.id}`}
                          >
                            <KeyRound className="w-3 h-3" /> <span className="hidden sm:inline">Reset</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7 gap-1 px-2 sm:px-3"
                            onClick={() => { setRoleDialog({ open: true, user: u }); setNewRole(u.role); }}
                            data-testid={`change-role-btn-${u.username || u.id}`}
                          >
                            <Shield className="w-3 h-3" /> <span className="hidden sm:inline">Role</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-400">No users found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialog.open} onOpenChange={(o) => setResetDialog({ ...resetDialog, open: o })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-slate-600">
              Resetting password for <strong>{resetDialog.user?.name}</strong> ({resetDialog.user?.username})
            </p>
            <div>
              <Label>New Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="pr-10"
                  data-testid="reset-password-input"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              onClick={handleResetPassword}
              disabled={actionLoading}
              className="w-full bg-[#0D2850] hover:bg-[#0B2246]"
              data-testid="confirm-reset-password-btn"
            >
              {actionLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={roleDialog.open} onOpenChange={(o) => setRoleDialog({ ...roleDialog, open: o })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-slate-600">
              Changing role for <strong>{roleDialog.user?.name}</strong> ({roleDialog.user?.username})
            </p>
            <div>
              <Label>New Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="mt-1" data-testid="new-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="hr">HR</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleUpdateRole}
              disabled={actionLoading}
              className="w-full bg-[#0D2850] hover:bg-[#0B2246]"
              data-testid="confirm-change-role-btn"
            >
              {actionLoading ? 'Updating...' : 'Update Role'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
